/**
 * Capital return multiple, the founder's book model under an honest planning
 * label (founder 2026-07-16). Each year your working capital redeploys
 * `turnsPerYear` times (12 ÷ your Capital-Return-Velocity months), and every
 * redeployment funds a customer who generates their full first-year value, so the estimate is
 * turns × (1-year value ÷ what it costs to get + serve that customer). His book:
 * 4 turns × ($300 ÷ $125) = "$1 → $9.60" = 960%.
 *
 * Two things a book keeps simple that this card lets you play with (his call):
 *   - a Revenue ↔ CM toggle — return on the top line vs on real margin,
 *   - two dials: capital recycled (how much recovered capital stays at work)
 *     and a CAC-increase rate (CAC creeps up as you scale — a rough planning number).
 *
 * The server hands over the verified primitives (1-year revenue value + 1-year CM
 * value per customer, the acquisition + overhead cost bar, and the turns/year from
 * CRV) plus the two saved dial positions; the card recomputes LIVE as the toggle
 * and dials move via the shared pure computeAnnualRoic (packages/shared), and
 * persists each dial per account. Run-rate by design — the note says so.
 *
 * NEW card, hidden from real clients until the founder approves the number.
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { computeAnnualRoic } from "@data-dashboard/shared";
import { useApiClient } from "../lib/api-context.js";
import { Card, CardHead } from "./cards.js";
import { Segmented } from "./Segmented.js";
import { CmGapNotice } from "./CmGapNotice.js";
import { LeverNeedWarning } from "./LeverNeedWarning.js";
import type { CmGap } from "../lib/card-requirements.js";

interface Roic {
  status: "live" | "needs_setup";
  needs?: string;
  revenueLtvPerCustomer: number | null;
  cmLtvPerCustomer: number | null;
  cacPerCustomer: number | null;
  overheadPerCustomer: number | null;
  turnsPerYear: number | null;
  reinvestmentRatePct: number;
  cacIncreasePer10PctVolume: number;
}
interface Pm {
  periodLabel: string;
  capabilities: { cmGap?: CmGap | null };
  roic: Roic;
}

const dollars = (n: number): string => `$${n.toFixed(2)}`;
const moneyRound = (n: number): string => `$${Math.round(n).toLocaleString()}`;

export function RoicCard(): React.ReactElement {
  const api = useApiClient();

  // The estimate is a 12-month run rate BY DESIGN, so it's LOCKED to the 1-year window
  // and ignores the sidebar date pill — otherwise a short-cycle window (worst of
  // all, "All time") inflates the turns-per-year and balloons the number into the
  // thousands of percent (founder 2026-07-15). One fixed fetch, always the 1-year read.
  const pmQ = useQuery({
    queryKey: ["profit-model", "roic-365d"],
    queryFn: () => api.get<Pm>("/dashboard/profit-model", { period: "365d" }),
  });
  const pm = pmQ.data;
  const roic = pm?.roic;

  // Revenue vs CM view. Default Revenue (always available; CM
  // needs product costs). Local-only, no persistence needed.
  const [basis, setBasis] = useState<"gross" | "cm">("gross");

  // Local dial positions for instant feedback; adopt the server's saved values
  // once they arrive (ui-prefs pattern), then local state leads.
  const [reinvest, setReinvest] = useState<number | null>(null);
  const [cacInc, setCacInc] = useState<number | null>(null);
  const adopted = useRef(false);
  useEffect(() => {
    if (adopted.current || roic === undefined) return;
    adopted.current = true;
    setReinvest(roic.reinvestmentRatePct);
    setCacInc(roic.cacIncreasePer10PctVolume);
  }, [roic]);
  const effReinvest = reinvest ?? roic?.reinvestmentRatePct ?? 100;
  const effCacInc = cacInc ?? roic?.cacIncreasePer10PctVolume ?? 4;

  const persistReinvest = (r: number): void => {
    setReinvest(r);
    api.post("/business-costs/reinvestment-rate", { reinvestmentRatePct: r }).catch(() => { /* stays local */ });
  };
  const persistCacInc = (r: number): void => {
    setCacInc(r);
    api.post("/business-costs/cac-increase-rate", { cacIncreasePer10PctVolume: r }).catch(() => { /* stays local */ });
  };

  const explain = [
    "<b>What it is:</b> A planning estimate of how much revenue or Contribution Margin each $1 of growth capital could generate as that dollar comes back and gets used again.",
    "<b>How it's calculated:</b> One turn = first-year value per customer ÷ the cost to get and serve that customer. The cost is acquisition cost plus overhead per customer. Turns per year = 12 ÷ your Capital Return Velocity months. The capital-recycled dial controls how much of the original dollar stays in the next turn. The CAC-increase dial raises the cost of later customers as you scale.",
    "<b>Example:</b> $300 of first-year revenue ÷ $125 to get and serve a customer = $2.40 generated for each $1 in one turn. If the same dollar turns four times and 100% stays in the model, it generates $9.60 of first-year revenue. CM uses margin instead of revenue.",
    "<b>Note:</b> This is not true Return on Invested Capital. A full calculation would need taxes, debt, working capital, and the full amount invested in the company. Revenue shows top-line output. CM is closer to economic return, but it is still not net operating profit.",
  ];

  const cmGap = pm?.capabilities.cmGap && pm.capabilities.cmGap.missingProducts > 0 ? pm.capabilities.cmGap : null;
  const live = roic?.status === "live";
  const cmAvailable = roic?.cmLtvPerCustomer !== null && roic?.cmLtvPerCustomer !== undefined;
  const activeBasis: "gross" | "cm" = basis === "cm" && !cmAvailable ? "gross" : basis;
  const ltv = live && roic ? (activeBasis === "cm" ? roic.cmLtvPerCustomer : roic.revenueLtvPerCustomer) : null;

  const multiple = live && roic
    ? computeAnnualRoic({
        ltvPerCustomer: ltv,
        cacPerCustomer: roic.cacPerCustomer,
        overheadPerCustomer: roic.overheadPerCustomer,
        turnsPerYear: roic.turnsPerYear,
        reinvestmentRatePct: effReinvest,
        cacIncreasePer10PctVolume: effCacInc,
      })
    : null;
  const valueWord = activeBasis === "cm" ? "Contribution Margin" : "revenue";

  return (
    <Card>
      <CardHead title="Capital return multiple · 12-month run rate" explain={explain} />
      {!pm ? (
        <p style={{ color: "var(--body)", fontSize: 13, marginTop: 10 }}>{pmQ.isError ? "Couldn't load this estimate. Refresh to try again." : "Working out your estimate…"}</p>
      ) : cmGap ? (
        <div style={{ marginTop: 10 }}><CmGapNotice gap={cmGap} /></div>
      ) : !live ? (
        <div style={{ marginTop: 10 }}><LeverNeedWarning needs={roic?.needs} /></div>
      ) : (
        <>
          {/* Revenue ↔ CM — his toggle to teach both angles. */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", margin: "10px 0 2px" }}>
            <Segmented<"gross" | "cm">
              value={activeBasis}
              onChange={(v) => setBasis(v)}
              options={[
                { value: "gross", label: "Revenue" },
                { value: "cm", label: "CM", disabled: !cmAvailable },
              ]}
            />
          </div>

          {/* Founder-specced headline: "$1 → (their number)" — the transformation. */}
          <div style={{ marginTop: 8 }}>
            <div className="dd-num" style={{ fontSize: 30, fontWeight: 800, color: "var(--num)", display: "flex", alignItems: "center", gap: 10 }}>
              <span>$1</span>
              <span aria-hidden="true" style={{ fontSize: 24, color: "var(--body-faint)", fontWeight: 700 }}>→</span>
              <span>{multiple === null ? "—" : dollars(multiple)}</span>
            </div>
            <div style={{ fontSize: 12.5, color: "var(--body)", marginTop: 4, lineHeight: 1.5 }}>
              For every <b>$1</b> you put into getting and serving customers, your current model generates an estimated <b className="dd-num">{multiple === null ? "—" : dollars(multiple)}</b> of {valueWord} at this speed.
            </div>
          </div>

          {/* The two dials — "the things that make it go up or down." */}
          <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
            <DialSlider
              label="Capital recycled"
              help="How much of the original capital that comes back stays in the model for the next turn. At 100%, all of it keeps working. At 0%, the model stops after the first turn."
              value={effReinvest}
              min={0}
              max={100}
              step={5}
              suffix="%"
              onChange={persistReinvest}
            />
            <DialSlider
              label="CAC increase as you scale"
              help="How much your cost to get a customer creeps up for every 10% more customers you acquire. A rough planning number — it varies a lot."
              value={effCacInc}
              min={0}
              max={30}
              step={1}
              suffix="% per +10% volume"
              onChange={persistCacInc}
            />
          </div>

          {roic && roic.turnsPerYear !== null && (
            <div style={{ fontSize: 11.5, color: "var(--text-faint)", marginTop: 10, lineHeight: 1.5 }}>
              Your capital turns over <b>{roic.turnsPerYear.toFixed(1)}×/year</b> (12 ÷ your {(12 / roic.turnsPerYear).toFixed(1)}-month cash cycle)
              {roic.cacPerCustomer !== null && roic.overheadPerCustomer !== null && ltv !== null && (
                <> · {moneyRound(ltv)} 1-year {activeBasis === "cm" ? "margin" : "revenue"} per customer vs {moneyRound(roic.cacPerCustomer + roic.overheadPerCustomer)} to get + serve them</>
              )}.
            </div>
          )}

          <div style={caveat}>
            Planning estimate only. This is not true Return on Invested Capital. Revenue shows top-line output. CM is closer to economic return, but it is still not net operating profit.
          </div>
        </>
      )}
    </Card>
  );
}

/** A labeled range dial with a live value readout — the founder's "play with it". */
function DialSlider(props: {
  label: string;
  help: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix: string;
  onChange: (v: number) => void;
}): React.ReactElement {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 12.5, color: "var(--text)", fontWeight: 600 }}>{props.label}</span>
        <span className="dd-num" style={{ fontSize: 13, color: "var(--num)", fontWeight: 800 }}>
          {props.value}
          <span style={{ fontSize: 11, color: "var(--body-faint)", fontWeight: 600 }}>{props.suffix}</span>
        </span>
      </div>
      <input
        type="range"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        onChange={(e) => props.onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: "var(--accent)", marginTop: 4, cursor: "pointer" }}
      />
      <div style={{ fontSize: 11, color: "var(--body-faint)", lineHeight: 1.4, marginTop: 1 }}>{props.help}</div>
    </div>
  );
}

const caveat: React.CSSProperties = {
  marginTop: 12, padding: "8px 12px", fontSize: 11.5, lineHeight: 1.45, color: "var(--body-faint)",
  background: "var(--surface-2)", border: "1px dashed var(--border)", borderRadius: "var(--r-sm)",
};
